/* Custom Input Component to be used with Formik */
import React, { useMemo, useEffect, useRef } from 'react';
import styled from 'styled-components/macro';
import PropTypes from 'prop-types';
import {
  Text,
  FormField,
  FormErrorMessage,
  FormFieldInfo,
} from 'components/atoms';
import { modifiersType } from 'types'
import { applyStyleModifiers } from 'styled-components-modifiers';
import { useFieldError } from 'hooks'

// this spacing is used below the error message, this makes the margin feel more natural
// when the error happears and disappears
const InputSpacingFix = styled.div`
  height: ${props => props.theme.spacing.m};
  width: 100%;
`;

const FIELD_CUSTOM_MODIFIERS = {
  stretch: () => `
   width: 100%;
  `,
};


const FieldCustom = ({
  className,
  title,
  field, // { name, value, onChange, onBlur }
  form: {
    touched, errors, isValid, isSubmitting, setFieldTouched, submitCount
  }, // also values, setXXXX, handleXXXX, dirty, isValid, status, etc.
  info,
  maxLetters,
  pre,
  shouldFocus,
  isDisabled,
  ...props
}) => {
  // focus on error after submit
  const fieldRef = useRef(null);
  useEffect(() => {
    if (isValid || !isSubmitting) return;
    const firstErrorKey = Object.keys(errors)[0];

    if (!isValid && firstErrorKey === field.name) {
      fieldRef.current.focus();
      setFieldTouched(field.name, true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSubmitting, isValid, errors]);

  // focus on mount
  useEffect(() => {
    if (shouldFocus && fieldRef && fieldRef.current) {
      fieldRef.current.focus();
    }
  }, [shouldFocus, fieldRef])

  const isError = useFieldError({
    touched, errors, field, submitCount
  })

  const modifiersField = useMemo(() => {
    if (isError) return ['error'];
    return []
  }, [isError]);

  const numberOfLetters = useMemo(
    // eslint-disable-next-line react/prop-types
    () => (field && field.value ? field.value.length : 0),
    [field]
  );

  const isOverMaxLetter = useMemo(() => {
    if (typeof maxLetters === 'number') return numberOfLetters > maxLetters;
    return false;
  }, [maxLetters, numberOfLetters]);

  const renderLettersError = useMemo(() => {
    const info = `${numberOfLetters}/${maxLetters}`;
    if (isOverMaxLetter) return <Text modifiers={['danger']}>{info}</Text>;
    return <Text modifiers={['light']}>{info}</Text>;
  }, [numberOfLetters, maxLetters, isOverMaxLetter]);

  const handleBlur = (args) => {
    if ('onBlur' in field && typeof field.onBlur === 'function') field.onBlur(args)
    if ('onBlur' in props && typeof props.onBlur === 'function') props.onBlur()
  }

  return (
    <Field className={className} isDisabled={isDisabled}>
      <Field.Top isDisabled={isDisabled}>
        {title && <Text modifiers={['base']}>{title}</Text>}
        {maxLetters && renderLettersError}
      </Field.Top>
      <Field.End isDisabled={isDisabled}>
        {pre && (
          <FieldPre isError={isError} onClick={() => fieldRef.current.focus()}>
            {pre}
          </FieldPre>
        )}
        <FormField
          type="text"
          innerRef={fieldRef}
          {...field}
          {...props}
          onBlur={handleBlur}
          modifiers={modifiersField}
        />
      </Field.End>
      {isError && <FormErrorMessage>{errors[field.name]}</FormErrorMessage>}
      {info && <FormFieldInfo>{info}</FormFieldInfo>}
      <InputSpacingFix />
    </Field>
  );
};

FieldCustom.propTypes = {
  className: PropTypes.string,
  title: PropTypes.string,
  field: PropTypes.shape({
    name: PropTypes.string,
    value: PropTypes.any,
    onBlur: PropTypes.func
  }).isRequired,
  form: PropTypes.shape({
    touched: PropTypes.shape({}),
    errors: PropTypes.shape({}),
    isSubmitting: PropTypes.bool,
    isValid: PropTypes.bool,
    setFieldTouched: PropTypes.func,
  }).isRequired,
  info: PropTypes.string,
  maxLetters: PropTypes.number,
  pre: PropTypes.string,
  onBlur: PropTypes.func,
  modifiers: modifiersType,
  shouldFocus: PropTypes.bool,
  isDisabled: PropTypes.bool,
};

FieldCustom.defaultProps = {
  className: '',
  title: null,
  info: null,
  maxLetters: null,
  pre: null,
  onBlur: () => { },
  modifiers: [],
  shouldFocus: false,
  isDisabled: false,
};

const Field = styled.div`
  opacity: ${props => (props.isDisabled ? 0.5 : 1)};
  cursor: ${props => (props.isDisabled ? 'not-allowed' : 'initial')};
`

Field.Top = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-between;
  pointer-events: ${props => (props.isDisabled ? 'none' : 'initial')};
`;

Field.End = styled.div`
  display: flex;
  cursor: pointer;
  pointer-events: ${props => (props.isDisabled ? 'none' : 'initial')};
`

const FieldPre = styled.div`
  color: ${props => props.theme.colors.base};
  padding: ${props => props.theme.spacing.xs2};
  font-size: ${props => props.theme.sizes.s};
  font-weight: ${props => props.theme.weight.xbase};
  background-color: ${props => props.theme.colors.bg};
  border-bottom: 1px solid ${props => (props.isError ? props.theme.colors.danger : props.theme.colors.base)};
`

export default styled(FieldCustom)`
  ${Text} {
    padding-bottom: ${props => props.theme.spacing.s};
  }
  ${applyStyleModifiers(FIELD_CUSTOM_MODIFIERS)};
`;
